import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Home, MessageSquare, LifeBuoy, Library } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useFeatureFlagResolved, FeatureFlag } from '@/services/featureFlag.service';
import { useRequestBadgeCount, usePendingKbCount } from '@/hooks/useNavBadges';
import { cn } from '@/lib/utils';
import { shouldRedirectToHome } from './mobileRoutes';

/**
 * The mobile app shell: a pared-down Pliable with three destinations — chat
 * with Ellie, requests, and the knowledge base — plus a home screen that pulls
 * the live bits of each onto one page.
 *
 * This replaces the sidebar shell wholesale rather than reflowing it. The
 * mobile app is a different information architecture, not a narrow desktop:
 * everything else (data sources, the semantic layer, analytics, develop mode)
 * needs a mouse and a wide canvas, so it isn't reachable here at all.
 */

type MobileTab = {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Route patterns that belong to this tab, for both the allowlist and the active state. */
  patterns: string[];
  badge?: number;
};

/**
 * The tabs this user can see, gated exactly as the sidebar gates the same items.
 *
 * `isResolved` reports whether those gates have actually answered yet. Both
 * flags default to false and land asynchronously, so a tab list read too early
 * is indistinguishable from a tenant that has those features switched off —
 * a difference that matters enormously to the route allowlist. Callers that
 * only render the tab bar can ignore it; the one that redirects cannot.
 *
 * Roles need no equivalent flag: AuthenticatedRootPage already blocks on auth
 * loading before the shell mounts, so `user` is settled by the time we look.
 *
 * Not exported: a second export alongside the component breaks Fast Refresh for
 * this file, and nothing outside the shell needs the list.
 */
function useMobileTabs(): { tabs: MobileTab[]; isResolved: boolean } {
  const { canViewDataManagement } = useUserRoles();
  const { value: isAiAssistantEnabled, isLoading: aiFlagLoading } =
    useFeatureFlagResolved(FeatureFlag.AI_ASSISTANT, false);
  const { value: useCasesEnabled, isLoading: useCasesFlagLoading } =
    useFeatureFlagResolved(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const requestBadgeCount = useRequestBadgeCount(canViewDataManagement);
  const pendingKbCount = usePendingKbCount(useCasesEnabled && canViewDataManagement);

  const tabs: MobileTab[] = [
    {
      label: 'Home',
      path: '/',
      icon: <Home className="h-5 w-5" />,
      patterns: ['/'],
    },
    ...(isAiAssistantEnabled
      ? [
          {
            label: 'Ellie',
            path: '/ellie',
            icon: <MessageSquare className="h-5 w-5" />,
            patterns: ['/ellie', '/ellie/conversation/:id'],
          },
        ]
      : []),
    ...(canViewDataManagement
      ? [
          {
            label: 'Requests',
            path: '/requests',
            icon: <LifeBuoy className="h-5 w-5" />,
            patterns: ['/requests', '/requests/:id'],
            badge: requestBadgeCount,
          },
        ]
      : []),
    ...(useCasesEnabled && canViewDataManagement
      ? [
          {
            label: 'Knowledge',
            path: '/knowledge-base',
            icon: <Library className="h-5 w-5" />,
            patterns: ['/knowledge-base', '/knowledge-base/article/:id'],
            badge: pendingKbCount,
          },
        ]
      : []),
  ];

  return { tabs, isResolved: !aiFlagLoading && !useCasesFlagLoading };
}

export default function MobileShell() {
  const { tabs, isResolved } = useMobileTabs();
  const location = useLocation();

  // Anything outside the three areas — a bookmark, a deep link from an email,
  // a redirect from some desktop-only flow — lands on home rather than on a
  // page that can't be used with a thumb. Held until the gates resolve, so a
  // deep link isn't thrown away while the answer is still "we don't know".
  if (shouldRedirectToHome({
    pathname: location.pathname,
    patterns: tabs.flatMap(tab => tab.patterns),
    isResolved,
  })) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col h-svh w-full bg-page-bg overflow-hidden">
      {/*
        Pages size themselves against the viewport (`h-screen`, `calc(100vh)`,
        `h-svh`) because under the desktop sidebar nothing else gives them a
        height. In here the shell owns the box, and a viewport-tall page would
        slide its own footer — a chat composer, a reply box — underneath the tab
        bar. Pinning the direct child to the content area fixes all of them at
        once; `!` beats the inline `style={{ height: ... }}` a couple of pages use.
      */}
      <main className="flex-1 min-h-0 overflow-hidden [&>*]:!h-full">
        <Outlet />
      </main>
      <MobileTabBar tabs={tabs} />
    </div>
  );
}

function MobileTabBar({ tabs }: { tabs: MobileTab[] }) {
  return (
    <nav
      className="flex-shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
      aria-label="Main"
    >
      <ul className="flex items-stretch">
        {tabs.map(tab => (
          <li key={tab.path} className="flex-1">
            <NavLink
              to={tab.path}
              // `end` only on Home: the other tabs stay lit on their detail routes.
              end={tab.path === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[11px] font-medium transition-colors',
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )
              }
            >
              <span className="relative">
                {tab.icon}
                {!!tab.badge && (
                  <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] leading-4 text-center">
                    {tab.badge > 9 ? '9+' : tab.badge}
                  </span>
                )}
              </span>
              <span className="truncate max-w-full">{tab.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
